import * as strings from 'owa-localize';
import { extractMailboxGuidFromEwsIdBytes } from './idConverter';

import { trace } from 'owa-trace';
import getFolderTable from '../selectors/getFolderTable';
import {
    SHARED_FOLDERS_TREE_TYPE,
    ARCHIVE_FOLDERS_TREE_TYPE,
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
} from './folderConstants';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Utility to extract mailbox guid from a given ews id
 * @param ewsId
 * @returns the mailbox guid
 */
export function getMailboxGuidFromEwsId(ewsId: string): string {
    try {
        if (!ewsId) {
            return null;
        }
        const decoded = atob(ewsId);
        const decodedLength = decoded.length;
        const array = new Uint8Array(new ArrayBuffer(decodedLength));

        for (let i = 0; i < decodedLength; i++) {
            array[i] = decoded.charCodeAt(i);
        }
        return extractMailboxGuidFromEwsIdBytes(array);
    } catch (e) {
        trace.error(
            'getMailboxGuidFromEwsId : conversion failed for id ' + ewsId + ' with error ' + e
        );
        return null;
    }
}

/**
 * Generates the anchor mailbox header
 * @param anchorMailboxGuid - mailbox guid
 * @param orgDomain - domain of the user's organization
 * @returns the mailbox header
 */
export function generateAnchorMailboxHeader(anchorMailboxGuid: string, orgDomain: string): string {
    if (!anchorMailboxGuid || !orgDomain) {
        return null;
    }
    const anchorMailboxHeaderFormat = '{0}@{1}';
    return strings.format(anchorMailboxHeaderFormat, anchorMailboxGuid, orgDomain);
}

/**
 * returns mailbox header for a folder id
 * @param folderId
 */
export function getAnchorMailboxHeaderForFolderId(folderId: string): string {
    if (!isFeatureEnabled('tri-anchorMailboxHeader')) {
        return null;
    }
    if (!folderId) {
        return null;
    }
    const sessionSettings = getUserConfiguration().SessionSettings;
    const folderTable = getFolderTable();
    const sourceFolder = folderTable && folderTable.get(folderId);
    // For archive mailbox, we already get the guid in session settings
    if (!sourceFolder && folderId !== ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID) {
        return null;
    }
    // Anchor mailbox is only mandatory for archive mailbox and shared folders
    if (
        folderId === ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID ||
        sourceFolder.treeType === ARCHIVE_FOLDERS_TREE_TYPE
    ) {
        return generateAnchorMailboxHeader(
            sessionSettings.ArchiveMailboxGuid,
            sessionSettings.OrganizationDomain
        );
    } else if (sourceFolder.treeType === SHARED_FOLDERS_TREE_TYPE) {
        return generateAnchorMailboxHeader(
            getMailboxGuidFromEwsId(folderId),
            sessionSettings.OrganizationDomain
        );
    }
    return null;
}

/**
 * returns mailbox header for a item id
 * @param itemId
 */
export function getAnchorMailboxHeaderForItemId(itemId: string): string {
    if (!isFeatureEnabled('tri-anchorMailboxHeader')) {
        return null;
    }
    if (!itemId) {
        return null;
    }
    const sessionSettings = getUserConfiguration().SessionSettings;
    const anchorMailboxGuid = getMailboxGuidFromEwsId(itemId);

    // No need to set anchorMailbox header if the anchormailboxGuid is same as user's guid in session settings
    if (anchorMailboxGuid === sessionSettings.MailboxGuid) {
        return null;
    }
    return generateAnchorMailboxHeader(anchorMailboxGuid, sessionSettings.OrganizationDomain);
}
